import { JobSchema } from './domain.js';
import { z } from 'zod';
import { NodeRoleSchema } from './nodes.js';
import { withJvmDefaults } from './jvm.js';
import { ServerRecordSchema, ServerStateSchema } from './domain.js';

export const RoleSchema = z.enum(['owner', 'admin', 'viewer']);
export const TokenScopeSchema = z.enum(['read', 'operate']);
export const UserSchema = z.object({
  id: z.string(),
  name: z.string(),
  login: z.string().optional(),
  role: RoleSchema,
  createdAt: z.string(),
  disabledAt: z.string().optional(),
});
export const MeSchema = z.object({
  user: UserSchema.nullable(),
  setupRequired: z.boolean(),
  devLogin: z.boolean(),
  steppedUpUntil: z.string().optional(),
  mcpUrl: z.string(),
});
const TokenMetadataSchema = z.object({
  id: z.string(),
  name: z.string(),
  createdAt: z.string(),
  lastUsedAt: z.string().optional(),
});
export const AccessTokenSchema = TokenMetadataSchema.extend({
  scopes: z.array(TokenScopeSchema),
  revokedAt: z.string().optional(),
});
export const InviteSchema = z.object({
  id: z.string(),
  role: RoleSchema,
  createdBy: z.string(),
  createdAt: z.string(),
  expiresAt: z.string(),
  usedAt: z.string().optional(),
});
/**
 * ノードのディスク使用量。pool は ZFS プール全体（システム・VM のディスク・毎晩のバックアップの /var/lib/vz を
 * 同じプールが分け合う）。どれかの領域だけを見ると、ほかが埋めた分に気づけない（2026-10-29 の pve-i7 は
 * バックアップの領域が埋まり、システムの領域は 18% のまま Proxmox の管理サービスが固まった）。
 * system は ZFS プールが無いノードでだけ使う / の使用量
 */
export const NodeDiskSchema = z.object({
  kind: z.enum(['pool', 'system']),
  name: z.string().optional(),
  usedGb: z.number(),
  totalGb: z.number(),
});
/**
 * ノードの上の VM（Panel が管理していないものも含む。テンプレートは除く）。閲覧だけの利用者にも見せる表示用で、
 * 配置の判断には使わない。kind: server = Panel のサーバー、test = クライアント Mod の起動テスト、
 * infra = Panel・edge・mail・共有 DB などの基盤（Proxmox のタグ mc-infra）、personal = 個人用（タグ personal）
 */
export const NodeVmSchema = z.object({
  vmid: z.number(),
  name: z.string(),
  kind: z.enum(['server', 'test', 'infra', 'personal', 'other']),
  /** kind が server のとき、そのサーバーの id */
  serverId: z.string().optional(),
  running: z.boolean(),
  /** VM に割り当てた CPU のうち使っている割合（0〜1） */
  cpu: z.number(),
  cores: z.number(),
  /** 使っているメモリ。止まっていれば 0 */
  memoryUsedMb: z.number(),
  /** 割り当てたメモリ（設定値） */
  memoryMb: z.number(),
});
export const NodeStatusSchema = z.object({
  role: NodeRoleSchema.default('world'),
  name: z.string(),
  online: z.boolean(),
  cpu: z.number(),
  memoryTotalMb: z.number(),
  memoryUsedMb: z.number(),
  memoryAllocatedMb: z.number(),
  /** 動いている VM だけの割り当て（止まっている VM はメモリを使わない）。読めないドライバーでは省く */
  memoryRunningMb: z.number().optional(),
  /** 一覧の画面と MCP だけで付ける。読めなかったものは省く */
  disks: z.array(NodeDiskSchema).optional(),
  /** 一覧の画面と MCP だけで付ける。読めなければ省く */
  vms: z.array(NodeVmSchema).optional(),
});
export const LiveStatusSchema = z.object({
  players: z.number(),
  maxPlayers: z.number(),
  playerNames: z.array(z.string()).optional(),
  tps: z.number().optional(),
  mspt: z.number().optional(),
  cpu: z.number().optional(),
  memoryUsedMb: z.number().optional(),
  dataDiskUsedGb: z.number().optional(),
  dataDiskTotalGb: z.number().optional(),
  updatedAt: z.string(),
});
/** 稼働中のはずなのに agent から監視値が届かない（ノードや VM が落ちた、agent が止まったなど）。lastSeen は最後に届いた時刻 */
export const UnreachableStatusSchema = z.object({ lastSeen: z.string().optional() });
/** サーバーの VM の電源（Minecraft の状態とは別）。stopping は「VM ごと停止」のシャットダウンを待っている間。読めなければ省く */
export const ServerVmPowerSchema = z.object({ running: z.boolean(), stopping: z.boolean().optional() });
const ServerViewRecordSchema = ServerRecordSchema.extend({
  live: LiveStatusSchema.optional(),
  unreachable: UnreachableStatusSchema.optional(),
  vm: ServerVmPowerSchema.optional(),
});
export const ServerViewSchema = ServerViewRecordSchema.transform((server): z.infer<typeof ServerViewRecordSchema> =>
  withJvmDefaults(server),
);
export const MetricPointSchema = z.object({
  t: z.string(),
  cpu: z.number().optional(),
  memoryMb: z.number().optional(),
  players: z.number().optional(),
  tps: z.number().optional(),
  mspt: z.number().optional(),
});
export const MetricRangeSchema = z.enum([
  '15m',
  '30m',
  '1h',
  '3h',
  '6h',
  '12h',
  '24h',
  '3d',
  '7d',
  '14d',
  '30d',
  '90d',
]);
/** グラフの期間: 決まった範囲（今まで）か、開始〜終了の日時。古い方は 90 日前まで */
export const MetricWindowSchema = z.union([
  MetricRangeSchema,
  z.object({ from: z.string().datetime({ offset: true }), to: z.string().datetime({ offset: true }) }).strict(),
]);
/** range か from・to のどちらか（両方なしは 1h） */
export const WebMetricsQuerySchema = z
  .object({
    range: MetricRangeSchema.optional(),
    from: z.string().datetime({ offset: true }).optional(),
    to: z.string().datetime({ offset: true }).optional(),
  })
  .strict()
  .refine((q) => (q.from === undefined) === (q.to === undefined) && !(q.range && q.from), {
    message: 'Use either range or both from and to',
  })
  .transform((q): MetricWindow => (q.from && q.to ? { from: q.from, to: q.to } : (q.range ?? '1h')));
/** range は決まった範囲で頼んだときだけ。from・to は実際に出した期間 */
export const MetricsResponseSchema = z.object({
  range: MetricRangeSchema.optional(),
  from: z.string(),
  to: z.string(),
  points: z.array(MetricPointSchema),
});
export const ConsoleEventSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('operation'), job: JobSchema }),
  z.object({ type: z.literal('line'), line: z.string() }),
  z.object({ type: z.literal('state'), state: ServerStateSchema }),
]);
export const NameSchema = z.string().trim().min(1).max(100);
export const TokenInputSchema = z.object({ name: NameSchema, scopes: z.array(TokenScopeSchema).min(1) }).strict();
export const UserPatchSchema = z.object({ role: RoleSchema.optional(), disabled: z.boolean().optional() }).strict();
export const InviteInputSchema = z.object({ role: RoleSchema }).strict();
export const ConsoleInputSchema = z
  .object({
    command: z
      .string()
      .min(1)
      .max(4096)
      .refine((s) => !/[\r\n\0]/.test(s)),
  })
  .strict();
export const ListQuerySchema = z
  .object({
    limit: z.coerce.number().int().min(1).max(1000).default(100),
    serverId: z.string().optional(),
    before: z.string().optional(),
  })
  .strict();
export const LogsQuerySchema = z.object({ tail: z.coerce.number().int().min(1).max(2000).default(200) }).strict();
export const SetupInputSchema = z.object({ token: z.string().min(1) }).strict();
export const InviteTokenParamsSchema = z.object({ token: z.string().min(1) }).strict();
export type Role = z.infer<typeof RoleSchema>;
export type TokenScope = z.infer<typeof TokenScopeSchema>;
export type User = z.infer<typeof UserSchema>;
/** 利用者の Minecraft アカウント（Java 版）。オーナー・管理者の分が管理者専用サーバーの許可リストに入る */
export const MinecraftAccountSchema = z.object({
  uuid: z.string(),
  userId: z.string(),
  name: z.string(),
  addedAt: z.string(),
});
export type MinecraftAccount = z.infer<typeof MinecraftAccountSchema>;
export const MinecraftAccountInputSchema = z
  .object({ name: z.string().regex(/^[A-Za-z0-9_]{3,16}$/, 'Minecraft の名前は英数字と _ の 3〜16 文字です') })
  .strict();
export type Me = z.infer<typeof MeSchema>;

export type AccessToken = z.infer<typeof AccessTokenSchema>;
export type Invite = z.infer<typeof InviteSchema>;
export type NodeStatus = z.infer<typeof NodeStatusSchema>;
export type NodeDisk = z.infer<typeof NodeDiskSchema>;
export type NodeVm = z.infer<typeof NodeVmSchema>;
export type LiveStatus = z.infer<typeof LiveStatusSchema>;
export type ServerView = z.infer<typeof ServerViewSchema>;
export type MetricPoint = z.infer<typeof MetricPointSchema>;
export type MetricRange = z.infer<typeof MetricRangeSchema>;
export type MetricWindow = z.infer<typeof MetricWindowSchema>;
export type MetricsResponse = z.infer<typeof MetricsResponseSchema>;
export type ConsoleEvent = z.infer<typeof ConsoleEventSchema>;
